import React, { useState, useRef, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Stepper,
    Step,
    StepLabel,
    Box,
    CircularProgress,
    Typography,
    Alert,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import ExportSelectionStep from './ExportSelectionStep';
import { generateDocxExport } from './ExportGenerator';
import { chartToBase64 } from './ExportUtils';
import ChartRenderer from './ChartRenderer';

/**
 * Main export dialog component
 * Handles the multi-step export process
 */
function ExportDialog({ open, onClose }) {
    const project = useSelector((state) => state.projects.selectedProject);
    const loading = useSelector((state) => state.projects.loading);
    const flatKpis = useSelector((state) => state.themes.flatKpis);
    const flatThemes = useSelector((state) => state.themes.flatThemes);
    
    const [activeStep, setActiveStep] = useState(0);
    const [selections, setSelections] = useState({
        projectInfo: true, // Always true
        loops: [],
        includeExplanationTextBoxes: false,
    });
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState(null);
    const [renderCharts, setRenderCharts] = useState(false);
    
    // Refs to store chart instances for image conversion
    const chartRefsRegistry = useRef({});
    
    const steps = ['Selecteer inhoud', 'Bevestig en exporteer'];
    
    // Get all chart configurations that need to be rendered
    const chartConfigs = React.useMemo(() => {
        const configs = [];
        for (const loopSelection of selections.loops || []) {
            // Add theme polar chart if includeThemeGraph is true
            if (loopSelection.includeThemeGraph) {
                configs.push({
                    loopId: loopSelection.loopId,
                    kpiId: 'theme-polar',
                    key: `${loopSelection.loopId}_theme-polar`,
                });
            }
            
            // Add regular graphs
            for (const graphSelection of loopSelection.graphs) {
                configs.push({
                    loopId: loopSelection.loopId,
                    kpiId: graphSelection.kpiId,
                    key: `${loopSelection.loopId}_${graphSelection.kpiId}`,
                });
            }
        }
        return configs;
    }, [selections]);
    
    // Render charts when we reach the confirmation step
    useEffect(() => {
        if (activeStep === 1 && chartConfigs.length > 0) {
            setRenderCharts(true);
        } else {
            setRenderCharts(false);
        }
    }, [activeStep, chartConfigs]);

    const handleNext = () => {
        if (activeStep === 0) {
            // Validate selections
            if (selections.loops.length === 0) {
                toast.error('Selecteer minimaal één ontwerpronde om te exporteren');
                return;
            }
        }
        setActiveStep((prevStep) => prevStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevStep) => prevStep - 1);
    };

    const handleClose = () => {
        if (!isGenerating) {
            setActiveStep(0);
            setSelections({ projectInfo: true, loops: [], includeExplanationTextBoxes: true });
            setError(null);
            onClose();
        }
    };

    const handleSelectionChange = (newSelections) => {
        setSelections(newSelections);
    };

    /**
     * Generate the export
     */
    const handleExport = async () => {
        setIsGenerating(true);
        setError(null);
        
        try {
            toast.info('Export wordt gegenereerd...', { autoClose: false, toastId: 'export-progress' });
            
            // Wait a bit for charts to fully render
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Convert charts to images
            const chartImages = [];
            
            for (const config of chartConfigs) {
                const chartRef = chartRefsRegistry.current[config.key];
                
                if (chartRef) {
                    try {
                        const refObj = chartRef.getChartRef();
                        if (refObj && refObj.current) {
                            const imageData = await chartToBase64(refObj);
                            chartImages.push({
                                loopId: config.loopId,
                                kpiId: config.kpiId,
                                imageData,
                            });
                        }
                    } catch (error) {
                        console.error(`Failed to convert chart ${config.key}:`, error);
                        // Continue with other charts
                    }
                }
            }
            
            // Generate DOCX
            await generateDocxExport(
                project,
                selections,
                flatKpis,
                flatThemes,
                chartImages
            );
            
            toast.dismiss('export-progress');
            toast.success('Export succesvol gedownload!', { autoClose: 400 });
            handleClose();
            
        } catch (error) {
            console.error('Export error:', error);
            setError(error.message || 'Er is een fout opgetreden bij het genereren van de export');
            toast.dismiss('export-progress');
            toast.error('Export mislukt. Probeer het opnieuw.');
        } finally {
            setIsGenerating(false);
        }
    };

    // Get summary of selections for confirmation step
    const getSelectionSummary = () => {
        const totalDesigns = selections.loops.reduce((sum, l) => sum + l.designs.length, 0);
        const totalGraphs = selections.loops.reduce((sum, l) => sum + l.graphs.length, 0);
        
        return {
            loops: selections.loops.length,
            designs: totalDesigns,
            graphs: totalGraphs,
            includesSetup: selections.loops.filter(l => l.includeSetup).length,
            includesScores: selections.loops.filter(l => l.includeScoresTable).length,
        };
    };

    const summary = getSelectionSummary();

    return (
        <Dialog 
            open={open} 
            onClose={handleClose} 
            maxWidth="md" 
            fullWidth
            disableEscapeKeyDown={isGenerating}
        >
            <DialogTitle>
                Export naar Word
            </DialogTitle>
            
            <DialogContent dividers>
                <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {isGenerating && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
                        <CircularProgress />
                        <Typography sx={{ ml: 2 }}>
                            Export wordt gegenereerd, dit kan even duren...
                        </Typography>
                    </Box>
                )}

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
                        <CircularProgress />
                        <Typography sx={{ ml: 2 }}>
                            Project wordt geladen...
                        </Typography>
                    </Box>
                )}

                {!isGenerating && !loading && (
                    <>
                        {activeStep === 0 && (
                            <ExportSelectionStep
                                selections={selections}
                                onSelectionChange={handleSelectionChange}
                            />
                        )}

                        {activeStep === 1 && (
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Bevestig je selectie
                                </Typography>
                                
                                <Typography variant="body2" color="text.secondary" paragraph>
                                    De volgende inhoud zal worden opgenomen in de export:
                                </Typography>

                                <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                                    <Typography variant="body1" gutterBottom>
                                        <strong>Projectsamenvatting:</strong> Altijd inbegrepen
                                    </Typography>

                                    <Typography variant="body1" gutterBottom>
                                        <strong>Tekstvelden voor uitleg:</strong>{' '}
                                        {selections.includeExplanationTextBoxes ? 'Ja' : 'Nee'}
                                    </Typography>
                                    
                                    <Typography variant="body1" gutterBottom>
                                        <strong>Ontwerprondes:</strong> {summary.loops}
                                    </Typography>
                                    
                                    <Typography variant="body1" gutterBottom>
                                        <strong>Ontwerpen:</strong> {summary.designs}
                                    </Typography>
                                    
                                    <Typography variant="body1" gutterBottom>
                                        <strong>Loop configuraties:</strong> {summary.includesSetup}
                                    </Typography>
                                    
                                    <Typography variant="body1" gutterBottom>
                                        <strong>Scores tabellen:</strong> {summary.includesScores}
                                    </Typography>
                                    
                                    <Typography variant="body1">
                                        <strong>Grafieken:</strong> {summary.graphs}
                                    </Typography>
                                </Box>

                                <Alert severity="info" sx={{ mt: 2 }}>
                                    Let op: Het genereren van de export kan enkele seconden tot een minuut duren, 
                                    afhankelijk van de hoeveelheid geselecteerde inhoud.
                                </Alert>
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>
            
            <DialogActions>
                <Button onClick={handleClose} disabled={isGenerating}>
                    Annuleren
                </Button>
                
                {activeStep > 0 && (
                    <Button onClick={handleBack} disabled={isGenerating}>
                        Vorige
                    </Button>
                )}
                
                {activeStep < steps.length - 1 ? (
                    <Button 
                        onClick={handleNext} 
                        variant="contained"
                        disabled={selections.loops.length === 0}
                    >
                        Volgende
                    </Button>
                ) : (
                    <Button 
                        onClick={handleExport} 
                        variant="contained" 
                        color="primary"
                        disabled={isGenerating || selections.loops.length === 0}
                    >
                        Exporteer
                    </Button>
                )}
            </DialogActions>

            {/* Hidden chart rendering area - render charts off-screen for image capture */}
            {renderCharts && (
                <Box sx={{ position: 'absolute', left: '-9999px', top: '-9999px', width: 800, height: 500 }}>
                    {chartConfigs.map((config) => (
                        <ChartRenderer
                            key={config.key}
                            ref={(ref) => {
                                if (ref) {
                                    chartRefsRegistry.current[config.key] = ref;
                                }
                            }}
                            loopId={config.loopId}
                            kpiId={config.kpiId}
                        />
                    ))}
                </Box>
            )}
        </Dialog>
    );
}

ExportDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
};

export default ExportDialog;

